import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import type { ReactElement } from 'react';

import { PricingDisplay } from '@/App/tiles/ForwardAccumulator/parts/PriceSummary/Pricing/PricingDisplay.tsx';
import { useForwardAccumulatorSelector } from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip';
import { useUserContext } from '@/context/UserContext';
import { executeForwardAccumulatorThunk } from '@/store/forwardAccumulator/thunk.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

interface PricingProps {
  tileId: string;
  isExecutable?: boolean;
}

export function Pricing({ tileId, isExecutable = false }: PricingProps): ReactElement {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const isTCCheckedStatus = useForwardAccumulatorSelector(
    (forwardAccumulator) => forwardAccumulator.tradeCapture.status === 'CHECKED',
  );

  const clientsWay = useForwardAccumulatorSelector((forwardAccumulator) => forwardAccumulator.properties.clientsWay);
  const { currentUser, tradePermission } = useUserContext();

  const isInternalSales = currentUser.userType === 'Internal Sales';
  const userEmail = currentUser.login;
  const canTrade = tradePermission === 'allowed';
  const showColoredPrices = useAppSelector((state) => state.userPreferences.showColoredPrices);

  const handleExecution = () => {
    if (isNotDefined(tileId)) {
      return;
    }

    dispatch(executeForwardAccumulatorThunk({ tileId, userEmail }));
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Executable price double-clicked',
      isDefined(tileId) ? tileId : 'unknown',
    );
  };

  const isReadyExecutable = isExecutable && isTCCheckedStatus;

  return isExecutable ? (
    <ForbiddenExecutionTooltip placement="right">
      <div
        className={clsx(
          'price-pricing',
          'py-4px',
          'executable-pricing',
          { 'colored-prices': showColoredPrices },
          clientsWay === 'Buy' ? 'buy' : 'sell',
          ...getRateComponentStyling({ executable: isReadyExecutable, cantTrade: !canTrade }),
          { canTrade },
        )}
        onDoubleClick={canTrade && isReadyExecutable ? handleExecution : undefined}
        data-e2e="price-executable"
        data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
      >
        <PricingDisplay isInternalSales={isInternalSales} />
      </div>
    </ForbiddenExecutionTooltip>
  ) : (
    <div
      className={clsx(
        'price-pricing',
        'py-4px',
        'border-top',
        'border-bottom',
        'indicative-pricing',
        clientsWay === 'Buy' ? 'buy' : 'sell',
        { 'colored-prices': showColoredPrices },
        'bg-lvl2',
        { canTrade },
      )}
      data-e2e="price-indicative"
      data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
    >
      <PricingDisplay isInternalSales={isInternalSales} />
    </div>
  );
}
